import {useDebugValue, useState} from 'react'

/**
 * Performs a shallow equality check on two arrays.
 *
 * Two arrays are considered shallowly equal if they are referentially equal or if they contain
 * the same number of items and each item is referentially equal to its corresponding item in
 * the other array.
 *
 * This is equivalent to the shallow equality check performed by React when comparing dependency
 * arrays of hooks.
 *
 * @param a The first array.
 * @param b The second array.
 * @returns Whether the two arrays are shallowly equal.
 */
function shallowArrayEqual(a: unknown[], b: unknown[]): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false
  }
  return true
}

/**
 * Creates a stateful value. If any dependencies have changed since the previous render, the
 * value is reinitialized to the current initial value.
 *
 * Note: this hook exists as a reference implementation but **this pattern is strongly
 * discouraged** because derived state implementations are very error-prone. Please carefully
 * consider alternative designs before using this hook.
 *
 * @param initialValue The initialization value.
 * @param dependencies An array of dependent values.
 * @returns A tuple containing a value and a setter, equivalent to the one returned by React's
 * `useState`.
 *
 * @see https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html
 * @see https://tacklebox.beta-p.kensho.com/useDependentReinitializingState_discouraged
 */
export default function useDependentReinitializingState_discouraged<T>(
  initialValue: T,
  dependencies: unknown[]
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState(initialValue)
  const [prevDependencies, setPrevDependencies] = useState(dependencies)

  useDebugValue(value)

  const shouldReinitialize = !shallowArrayEqual(dependencies, prevDependencies)

  // enqueue an update to reinitialize the value
  if (shouldReinitialize) {
    setValue(initialValue)
    setPrevDependencies(dependencies)
  }

  // that update will ensure that React bails out before rendering the subtree and immediately
  // re-renders with the reinitialized value, but the reinitialization should also be reflected in
  // the current render in case the containing render function makes assumptions about consistency
  const currentValue = shouldReinitialize ? initialValue : value

  return [currentValue, setValue]
}
